
import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Language } from '../types';
import { supabase, syncUserProfile, signOut as supabaseSignOut, signInWithGoogle as googleLogin, signInWithKakao as kakaoLogin, signInWithNaver as naverLogin, signUpWithEmail, signInWithEmail } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, pass: string) => Promise<void>;
  signup: (name: string, email: string, pass: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithKakao: () => Promise<void>;
  loginWithNaver: () => Promise<void>;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  login: async () => {},
  signup: async () => {},
  loginWithGoogle: async () => {},
  loginWithKakao: async () => {},
  loginWithNaver: async () => {},
  logout: () => {},
  updateUser: () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getCurrentLang = (): Language => {
    const saved = localStorage.getItem('app_lang') as Language;
    if (saved) return saved;
    const browserLang = navigator.language || (navigator as any).userLanguage;
    return browserLang?.startsWith('ko') ? 'ko' : 'en';
  };

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await supabaseSignOut();
    } catch (e) {
        console.error("Logout error:", e);
    } finally {
      setUser(null);
      setIsLoading(false);
      if (window.navigateTo) window.navigateTo('/login');
    }
  };

  const handleAuthSession = async (sbUser: any, shouldWaitProfile: boolean = false) => {
    if (!sbUser) {
      setUser(null);
      return;
    }

    // 1. 임시 프로필 설정 (Metadata에 role이 있으면 반영)
    const tempProfile: User = {
        id: sbUser.id,
        name: sbUser.user_metadata?.full_name || sbUser.user_metadata?.name || 'User',
        email: sbUser.email || '',
        avatar: sbUser.user_metadata?.avatar_url || '',
        isPro: false,
        joinDate: sbUser.created_at || new Date().toISOString(),
        lang: getCurrentLang(),
        role: sbUser.user_metadata?.role || 'user' 
    };
    
    setUser(tempProfile);

    // 2. DB 프로필 동기화
    const syncTask = async () => {
      try {
        const profile = await syncUserProfile(
          sbUser.id,
          sbUser.email || '',
          tempProfile.name,
          tempProfile.avatar || '',
          getCurrentLang()
        );
        if (profile) setUser(profile);
      } catch (e) {
        console.warn("DB Profile sync failed:", e);
      }
    };

    if (shouldWaitProfile) {
      await syncTask();
    } else {
      syncTask(); 
    }
  };

  useEffect(() => {
    let mounted = true;

    const initSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        if (session && mounted) {
          // 세션이 있는 경우에만 DB 동기화를 기다림
          await handleAuthSession(session.user, true);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Session initialization error:", error);
        setUser(null);
      } finally {
        // 성공하든 실패하든 세션 체크가 끝나면 반드시 로딩 해제
        if (mounted) setIsLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (session) await handleAuthSession(session.user, false);
      } else if (event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
          if (!session) {
            setUser(null);
            setIsLoading(false);
            // 요원 도구 페이지인 경우 로그인 페이지로 리다이렉트
            const path = window.getCurrentPath ? window.getCurrentPath() : window.location.pathname;
            if (path.startsWith('/mypage') || path.startsWith('/admin')) {
                if (window.navigateTo) window.navigateTo('/login');
            }
          }
      }
    });

    initSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const loginWithGoogle = async () => {
    setIsLoading(true);
    try { await googleLogin(); } catch (error) { setIsLoading(false); throw error; }
  };

  const loginWithKakao = async () => {
    setIsLoading(true);
    try { await kakaoLogin(); } catch (error) { setIsLoading(false); throw error; }
  };

  const loginWithNaver = async () => {
    setIsLoading(true);
    try { await naverLogin(); } catch (error) { setIsLoading(false); throw error; }
  };

  const login = async (email: string, pass: string) => {
    setIsLoading(true);
    try {
      const sbUser = await signInWithEmail(email, pass);
      if (sbUser) await handleAuthSession(sbUser, true); 
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (name: string, email: string, pass: string) => {
    setIsLoading(true);
    try {
      const sbUser = await signUpWithEmail(email, pass, name);
      if (sbUser) await handleAuthSession(sbUser, true);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (data: Partial<User>) => {
      setUser(prev => prev ? { ...prev, ...data } as User : null);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, signup, loginWithGoogle, loginWithKakao, loginWithNaver, logout: handleLogout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
